import { HamburgerIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  useBreakpointValue,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { Link as RouterLink } from 'react-router-dom';
import logo from "../assets/icon.png";
import { JETBRAINS_PLUGIN_URL } from "../constants/urls";

export default function NavBar() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const listDisplay = useBreakpointValue({ base: "none", lg: "flex" });
  const menuDisplay = useBreakpointValue({ base: "flex", lg: "none" });

  const extItems = [
    {
      label: "Pricing",
      link: "/pricing",
      isExternal: false
    },
    {
      label: "Features",
      link: "#features",
      isExternal: false,
      isScroll: true
    },
    {
      label: "Enterprise",
      link: "/enterprise",
      isExternal: false
    },
    // {
    //   label: "Blog",
    //   link: "/blog",
    //   isExternal: false
    // },
    {
      label: "Docs",
      link: "https://docs.sweep.dev/",
      isExternal: true
    },
  ]

  return (
    <Box
      as="nav"
      bg="bg-surface"
      width="full"
      p={4}
      position="fixed"
      top={0}
      zIndex={1000}
      backdropFilter="blur(3px)"
      backgroundColor="transparent"
    >
      <Flex justify="center" align="center">
        <HStack spacing={4} justify="center" maxW="1400px" width="full">
          {/* Logo */}
          <Button
            as={Link}
            variant="ghost"
            to="/"
            py={6}
            fontSize="xl"
          >
            <Image src={logo} alt="logo" width={10} borderRadius={12} />
            Sweep AI
          </Button>

          {/* Desktop Navigation */}
          <HStack spacing={4} display={listDisplay} alignItems="center" flex="1">
            <Box flex="0.5" /> {/* Left spacer */}
            <HStack spacing={4}>
              {extItems.map((item) => {
                if (item.isExternal) {
                  return (
                    <Button
                      key={item.label}
                      as="a"
                      variant="ghost"
                      href={item.link}
                      target="_blank"
                      fontSize="xl"
                      py={6}
                      display="flex"
                      alignItems="center"
                    >
                      {item.label}
                    </Button>
                  );
                } else if (item.isScroll) {
                  return (
                    <Button
                      key={item.label}
                      variant="ghost"
                      fontSize="xl"
                      py={6}
                      display="flex"
                      alignItems="center"
                      onClick={() => {
                        // Check if we're on the home page
                        if (window.location.pathname !== '/') {
                          // Navigate to home page first, then scroll
                          window.location.href = '/#features';
                        } else {
                          // Already on home page, just scroll
                          const element = document.getElementById('features');
                          if (element) {
                            const navbarHeight = 80; // Account for fixed navbar height
                            const elementPosition = element.offsetTop - navbarHeight;
                            window.scrollTo({ top: elementPosition, behavior: 'smooth' });
                          }
                        }
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                } else {
                  return (
                    <Button
                      key={item.label}
                      as={RouterLink}
                      variant="ghost"
                      to={item.link}
                      fontSize="xl"
                      py={6}
                      display="flex"
                      alignItems="center"
                    >
                      {item.label}
                    </Button>
                  );
                }
              })}
            </HStack>
            <Box flex="1" display="flex" justifyContent="flex-end"> {/* Right spacer with flex-end */}
              <Button
                colorScheme="purple"
                p={4}
                bg="purple.700"
                _hover={{ bg: "purple.500" }}
                color="white"
                onClick={() => window.open(JETBRAINS_PLUGIN_URL)}
                fontSize="lg"
                width="fit-content"
                display="flex"
                alignItems="center"
              >
                Install Sweep
              </Button>
            </Box>
          </HStack>

          {/* Mobile Menu Button */}
          <IconButton
            display={menuDisplay}
            aria-label="Open menu"
            icon={<HamburgerIcon />}
            variant="ghost"
            onClick={onOpen}
          />
        </HStack>
      </Flex>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent
          minW={{ base: "100%", sm: "100%" }}
          maxW={{ base: "100%", sm: "100%" }}
          bg="gray.900"
          borderLeft="1px solid"
          borderColor="whiteAlpha.200"
        >
          <DrawerCloseButton color="white" />
          <DrawerHeader borderBottomWidth="1px" borderColor="whiteAlpha.200" color="white">
            Sweep AI
          </DrawerHeader>
          <DrawerBody p={4}>
            <VStack
              spacing={4}
              align="stretch"
              mt={4}
              width="100%"
              minW="100%"
            >
              {extItems.map((item) => {
                if (item.isExternal) {
                  return (
                    <Button
                      key={item.label}
                      as="a"
                      href={item.link}
                      target="_blank"
                      variant="ghost"
                      width="100%"
                      minW="100%"
                      justifyContent="flex-start"
                      onClick={onClose}
                      color="white"
                      fontSize="lg"
                      py={6}
                      _hover={{ bg: "whiteAlpha.200" }}
                    >
                      {item.label}
                    </Button>
                  );
                } else if (item.isScroll) {
                  return (
                    <Button
                      key={item.label}
                      variant="ghost"
                      width="100%"
                      minW="100%"
                      justifyContent="flex-start"
                      onClick={() => {
                        // Check if we're on the home page
                        if (window.location.pathname !== '/') {
                          // Navigate to home page first, then scroll
                          window.location.href = '/#features';
                        } else {
                          // Already on home page, just scroll
                          const element = document.getElementById('features');
                          if (element) {
                            const navbarHeight = 80; // Account for fixed navbar height
                            const elementPosition = element.offsetTop - navbarHeight;
                            window.scrollTo({ top: elementPosition, behavior: 'smooth' });
                          }
                        }
                        onClose();
                      }}
                      color="white"
                      fontSize="lg"
                      py={6}
                      _hover={{ bg: "whiteAlpha.200" }}
                    >
                      {item.label}
                    </Button>
                  );
                } else {
                  return (
                    <Button
                      key={item.label}
                      as={RouterLink}
                      to={item.link}
                      variant="ghost"
                      width="100%"
                      minW="100%"
                      justifyContent="flex-start"
                      onClick={onClose}
                      color="white"
                      fontSize="lg"
                      py={6}
                      _hover={{ bg: "whiteAlpha.200" }}
                    >
                      {item.label}
                    </Button>
                  );
                }
              })}
              <Button
                as={RouterLink}
                to="/pricing"
                variant="ghost"
                width="100%"
                minW="100%"
                justifyContent="flex-start"
                onClick={onClose}
                color="white"
                fontSize="lg"
                py={6}
                _hover={{ bg: "whiteAlpha.200" }}
              >
                Pricing
              </Button>
              <Button
                colorScheme="purple"
                p={4}
                bg="purple.700"
                _hover={{ bg: "purple.500" }}
                color="white"
                width="100%"
                minW="100%"
                fontSize="lg"
                onClick={() => {
                  window.open(JETBRAINS_PLUGIN_URL);
                  onClose();
                }}
              >
                Install Sweep
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}